import { ArrowDownOutlined } from '@ant-design/icons'
import appStore from '@stores/appStore'
import type { AssistantSession } from '@stores/assistantSession'
import assistantStore, { AssistantSuggestedQuery } from '@stores/assistantStore'
import { Alert, Button } from 'antd'
import dayjs from 'dayjs'
import { observer } from 'mobx-react'
import { useEffect, useRef, useState } from 'react'

import { pageContextNoun, pageContextTool } from './assistantPageContext'
import { useAssistantSession } from './AssistantSessionContext'
import BriefingCard from './BriefingCard'
import {
  AVAILABILITY_ERROR_CODES,
  errorText,
  unavailableText,
} from './errorMessages'
import MessageBubble from './MessageBubble'
import { useAssistantPlacement } from './placements/PlacementContext'
import ToolActivityIndicator from './ToolActivityIndicator'
import { AssistantAvatar, KW, NoticeStrip, SuggestionButton } from './ui'

/** Within this distance of the end of the list the user counts as reading the newest content. */
const STICK_TO_BOTTOM_PX = 80

const scrollToEnd = (el: HTMLElement, smooth: boolean) => {
  if (smooth && typeof el.scrollTo === 'function') {
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  } else {
    el.scrollTop = el.scrollHeight
  }
}

/** How many "Try asking" suggestions an empty chat shows before "Show more". */
const VISIBLE_SUGGESTIONS = 4

const FALLBACK_SUGGESTIONS: AssistantSuggestedQuery[] = [
  { category: 'Cases', text: 'Which cases are assigned to me?' },
  { category: 'Cases', text: 'Show IME cases with appointments this week' },
]

// Shown on a case screen, each only while the tool that answers it is on for
// the tenant (a skill switched off by the host hides its suggestions too).
const CASE_SUGGESTIONS: (AssistantSuggestedQuery & { toolName: string })[] = [
  {
    category: 'This case',
    text: 'Summarize this case',
    toolName: 'get_case_detail',
  },
  {
    category: 'This case',
    text: 'What documents does this case have?',
    toolName: 'list_case_attachments',
  },
]

const getSuggestions = (
  session: AssistantSession
): AssistantSuggestedQuery[] => {
  const list: AssistantSuggestedQuery[] = []
  // activePageContext is the record a question would be asked about: none once the chip is
  // removed, and none of a kind whose tool is off.
  // A list on screen names no record: nothing is suggested about "this case".
  const screen = session.activePageContext
  const context = screen?.list ? null : screen
  if (context?.type === 'case') {
    list.push(
      ...CASE_SUGGESTIONS.filter(s => assistantStore.canUseTool(s.toolName))
    )
  } else if (context && pageContextTool(context.type)) {
    const noun = pageContextNoun(context.type)
    list.push({
      category: `This ${noun}`,
      text: `Summarize this ${noun}`,
    })
  }
  list.push(
    ...(assistantStore.suggestions.length
      ? assistantStore.suggestions
      : FALLBACK_SUGGESTIONS.filter(() =>
          assistantStore.canUseTool('search_cases')
        ))
  )
  return list
}

const getGreeting = () => {
  const hour = dayjs().hour()
  const timeOfDay = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening'
  const name = appStore.currentUser?.name || appStore.currentUser?.userName
  return `Good ${timeOfDay}${name ? `, ${name}` : ''}`
}

/**
 * Why the assistant cannot be used, shown in place of what it would offer. The reason can end
 * while the page is open (the host switches the feature back on): "Check again" asks.
 */
const UnavailableNotice = observer(() => {
  const reason = assistantStore.unavailableReason
  // What the last "Check again" came to, while the notice is still there: an answer that
  // changes nothing on screen would otherwise look like a button that does nothing.
  const [check, setCheck] = useState<
    'checking' | 'unchanged' | 'failed' | null
  >(null)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  if (!reason) {
    return null
  }

  const checkAgain = async () => {
    setCheck('checking')
    const answered = await assistantStore.checkAvailability()
    if (mountedRef.current) {
      setCheck(answered ? 'unchanged' : 'failed')
    }
  }

  return (
    <div role="status">
      <NoticeStrip tone="amber">
        {unavailableText(reason)}{' '}
        <button
          type="button"
          onClick={checkAgain}
          disabled={check === 'checking'}
          className="cursor-pointer border-0 bg-transparent p-0 font-bold underline"
          style={{ color: 'inherit', fontFamily: 'inherit' }}
        >
          {check === 'checking' ? 'Checking…' : 'Check again'}
        </button>
        {check === 'unchanged' && ' Checked just now: still not available.'}
        {check === 'failed' && ' Could not check. Please try again.'}
      </NoticeStrip>
    </div>
  )
})

const Greeting = () => (
  <div className="mb-1 flex items-center gap-[10px]">
    <AssistantAvatar size={30} />
    <div className="text-[19px] font-extrabold" style={{ color: KW.ink }}>
      {getGreeting()}
    </div>
  </div>
)

/**
 * The empty chat: what the placement's welcome offers (useAssistantPlacement). The drawer shows
 * all of it; a placement that turns every part off shows its intro line only (if it has one),
 * and leaves the room to its quick questions. The "not available" notice is not part of it: the
 * list always shows that one under it.
 */
const EmptyState = observer(() => {
  const session = useAssistantSession()
  const { welcome } = useAssistantPlacement()
  const [showAllSuggestions, setShowAllSuggestions] = useState(false)

  const intro = welcome.intro ? (
    <div className="mb-4 text-[13px]" style={{ color: KW.muted }}>
      {welcome.intro}
    </div>
  ) : null

  // Not available: no briefing, guide or suggestions to offer — the notice under the greeting
  // says why (see UnavailableNotice in the list).
  if (assistantStore.unavailableReason) {
    return welcome.greeting ? (
      <div className="kw-up">
        <Greeting />
      </div>
    ) : null
  }

  // The first few are enough to start from; the rest are one click away.
  const suggestions = welcome.tryAsking ? getSuggestions(session) : []
  const hiddenCount = Math.max(0, suggestions.length - VISIBLE_SUGGESTIONS)
  const shown = showAllSuggestions
    ? suggestions
    : suggestions.slice(0, VISIBLE_SUGGESTIONS)

  const groups = shown.reduce<Record<string, AssistantSuggestedQuery[]>>(
    (acc, suggestion) => {
      const category = suggestion.category || 'General'
      acc[category] = acc[category] ?? []
      acc[category].push(suggestion)
      return acc
    },
    {}
  )

  if (
    !welcome.greeting &&
    !welcome.briefing &&
    !welcome.guideLink &&
    !welcome.tryAsking &&
    !intro
  ) {
    return null
  }

  return (
    <div className="kw-up">
      {welcome.greeting && (
        <>
          <Greeting />
          <div className="mb-4 pl-10 text-[13px]" style={{ color: KW.muted }}>
            {dayjs().format('dddd, D MMMM YYYY')} · here&apos;s what I can help
            with today.
          </div>
        </>
      )}

      {intro}

      {welcome.briefing && <BriefingCard />}

      {welcome.guideLink && (
        <button
          type="button"
          onClick={() => session.openGuide()}
          className="mb-4 cursor-pointer border-0 bg-transparent p-0 text-xs font-bold"
          style={{ color: KW.teal500, fontFamily: 'inherit' }}
        >
          New here? Read the guide: what I can do, commands and example
          questions →
        </button>
      )}

      {Object.keys(groups).length > 0 && (
        <div
          className="mb-3 text-[11px] font-extrabold uppercase tracking-widest"
          style={{ color: KW.muted }}
        >
          Try asking
        </div>
      )}
      {Object.entries(groups).map(([category, items]) => (
        <div key={category} className="mb-[14px]">
          <div className="mb-2 text-xs font-bold" style={{ color: '#5a6474' }}>
            {category}
          </div>
          <div className="flex flex-col gap-2">
            {items.map(suggestion => (
              <SuggestionButton
                key={suggestion.text}
                label={`${suggestion.text}${suggestion.prefillOnly ? '…' : ''}`}
                disabled={!suggestion.prefillOnly && session.isInputLocked}
                onClick={() => session.applySuggestion(suggestion)}
              />
            ))}
          </div>
        </div>
      ))}
      {hiddenCount > 0 && (
        <button
          type="button"
          aria-expanded={showAllSuggestions}
          onClick={() => setShowAllSuggestions(!showAllSuggestions)}
          className="mb-2 cursor-pointer border-0 bg-transparent p-0 text-xs font-bold"
          style={{ color: KW.teal500, fontFamily: 'inherit' }}
        >
          {showAllSuggestions
            ? 'Show fewer examples'
            : `Show ${hiddenCount} more ${
                hiddenCount === 1 ? 'example' : 'examples'
              }`}
        </button>
      )}
    </div>
  )
})

const MessageList = () => {
  const session = useAssistantSession()
  const scrollRef = useRef<HTMLDivElement>(null)
  // The list follows new content only while the user is at its end: someone who scrolled up to
  // read is left where they are, and offered "Jump to latest" instead.
  const stickRef = useRef(true)
  const lastScrollTopRef = useRef(0)
  const seenRef = useRef<{
    count: number
    conversationId: string | null
    notice: string | null
  }>()
  // Where the list stood when "Load earlier messages" was pressed (see loadEarlier).
  const beforeEarlierRef = useRef<{ top: number; height: number } | null>(null)
  const earlierButtonRef = useRef<HTMLButtonElement>(null)
  const messagesRef = useRef<HTMLDivElement>(null)
  // Earlier messages are being put above the ones on screen: the list does not announce them
  // (a page is up to 200 messages), one line says how many arrived instead.
  const [prepending, setPrepending] = useState(false)
  const [earlierNotice, setEarlierNotice] = useState('')
  const [showJump, setShowJump] = useState(false)
  const {
    messages,
    streamingContent,
    toolActivity,
    status,
    errorCode,
    loadingConversation,
    activeConversationId,
  } = session
  const { unavailableReason } = assistantStore

  const onScroll = () => {
    const el = scrollRef.current
    if (!el) {
      return
    }
    const top = el.scrollTop
    const movedUp = top < lastScrollTopRef.current
    lastScrollTopRef.current = top
    const distance = el.scrollHeight - top - el.clientHeight
    if (movedUp) {
      // Only a move towards the top is the user leaving the end, and any such move is: a wheel
      // notch, a drag or an arrow key moves a few pixels at a time, and text that streams in
      // would pull the list back between two of them. Being away from the end without one is
      // the list's own doing: a smooth scroll on its way down, or content that grew. (A list
      // that got shorter is moved up by the browser, but onto its end: not a move away.)
      if (distance > 1) {
        stickRef.current = false
      }
    } else if (distance <= STICK_TO_BOTTOM_PX) {
      stickRef.current = true
      setShowJump(false)
    }
  }

  useEffect(() => {
    const el = scrollRef.current
    if (!el) {
      return
    }
    const seen = seenRef.current
    // The warning of a failed turn and the "not available" notice are shown under the messages,
    // in this list: they are new content at its end like a message is.
    const notice = unavailableReason ?? errorCode
    seenRef.current = {
      count: messages.length,
      conversationId: activeConversationId,
      notice,
    }
    const opened = !seen || seen.conversationId !== activeConversationId
    // Earlier messages were put above the ones on screen: nothing new at the end. What the user
    // was reading stays where it was, by as much as the list grew above it. (Set, not added to:
    // a browser that anchors the scroll position itself has already arrived there.)
    const earlier = beforeEarlierRef.current
    if (earlier && !opened && !!seen && messages.length > seen.count) {
      beforeEarlierRef.current = null
      el.scrollTop = earlier.top + (el.scrollHeight - earlier.height)
      lastScrollTopRef.current = el.scrollTop
      return
    }
    const added = !!seen && messages.length > seen.count
    // A warning that was closed leaves nothing new to bring into view.
    if (
      !!seen &&
      !opened &&
      !added &&
      seen.notice !== null &&
      notice === null
    ) {
      return
    }
    // The user's own question is always brought into view, wherever they were reading.
    const asked = added && messages[messages.length - 1]?.role === 'User'
    if (opened || asked) {
      stickRef.current = true
    }
    if (!stickRef.current) {
      setShowJump(true)
      return
    }
    setShowJump(false)
    // A new message slides into view; a conversation that opens and text that streams in are
    // followed without animation (a smooth scroll per frame never arrives).
    scrollToEnd(el, added && !opened)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    messages.length,
    streamingContent,
    toolActivity,
    activeConversationId,
    errorCode,
    unavailableReason,
  ])

  const loadEarlier = async () => {
    const el = scrollRef.current
    beforeEarlierRef.current = el
      ? { top: el.scrollTop, height: el.scrollHeight }
      : null
    const count = session.messages.length
    setEarlierNotice('')
    setPrepending(true)
    await session.loadEarlierMessages()
    const loaded = session.messages.length - count
    // Nothing was added (the load failed or was dropped): there is no growth to make up for.
    if (loaded <= 0) {
      beforeEarlierRef.current = null
      setPrepending(false)
      return
    }
    setEarlierNotice(
      loaded === 1
        ? '1 earlier message loaded'
        : `${loaded} earlier messages loaded`
    )
  }

  // Once the earlier messages are on screen: the focus stays with the button when there are
  // more to load, and goes to the messages when the button is gone (not to nowhere); then the
  // list announces again.
  useEffect(() => {
    if (!earlierNotice) {
      return undefined
    }
    const target = earlierButtonRef.current ?? messagesRef.current
    target?.focus({ preventScroll: true })
    const timer = setTimeout(() => setPrepending(false), 0)
    return () => clearTimeout(timer)
  }, [earlierNotice])

  const jumpToLatest = () => {
    const el = scrollRef.current
    if (!el) {
      return
    }
    stickRef.current = true
    setShowJump(false)
    scrollToEnd(el, true)
    // The button is gone once it was used: the focus goes on to the composer, not nowhere.
    session.requestComposerFocus()
  }

  // Said by the notice instead, once the capabilities confirm the assistant is unavailable.
  const showError =
    !!errorCode &&
    !(unavailableReason && AVAILABILITY_ERROR_CODES.has(errorCode))

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="kw-scroll min-h-0 flex-1 overflow-y-auto px-4 pb-[10px] pt-5"
        aria-live={prepending ? 'off' : 'polite'}
        aria-relevant="additions"
      >
        {/* A conversation being opened (the standalone page's ?conversationId=) is not an empty
          chat: no greeting or suggestions until it has arrived or failed. */}
        {loadingConversation ? (
          <div role="status">
            <ToolActivityIndicator label="Loading conversation…" />
          </div>
        ) : (
          messages.length === 0 && status === 'idle' && <EmptyState />
        )}

        {session.hasEarlierMessages && !loadingConversation && (
          <div className="mb-4 flex justify-center">
            <button
              ref={earlierButtonRef}
              type="button"
              className="kw-history-btn"
              disabled={session.loadingEarlierMessages || session.isBusy}
              onClick={loadEarlier}
            >
              {session.loadingEarlierMessages
                ? 'Loading…'
                : 'Load earlier messages'}
            </button>
          </div>
        )}

        {/* Named, so that the focus it is given after "Load earlier messages" lands on
          something a screen reader can say. */}
        <div
          ref={messagesRef}
          tabIndex={-1}
          role="group"
          aria-label="Messages"
          className="flex flex-col gap-[18px] outline-none"
        >
          {messages.map((message, index) => (
            <MessageBubble key={message.id ?? index} message={message} />
          ))}

          {/* Hidden from screen readers while streaming: the finished message is added to the
            list above and announced once, instead of every token. */}
          {streamingContent && (
            <div aria-hidden="true">
              <MessageBubble
                message={{
                  role: 'Assistant',
                  content: streamingContent,
                  citations: session.turn.citations,
                  parts: session.turn.parts,
                }}
                streaming
              />
            </div>
          )}

          {toolActivity && <ToolActivityIndicator label={toolActivity} />}

          {status === 'sending' && !toolActivity && !streamingContent && (
            <ToolActivityIndicator />
          )}
        </div>

        <UnavailableNotice />

        {showError && errorCode && (
          <Alert
            className="mt-3"
            type="warning"
            showIcon
            closable
            onClose={session.dismissError}
            action={
              session.canRetry ? (
                <Button
                  size="small"
                  type="link"
                  onClick={() => session.retryLastTurn()}
                >
                  Retry
                </Button>
              ) : undefined
            }
            message={errorText(errorCode, session.errorMessage)}
          />
        )}
      </div>

      {/* Outside the list, which is silent while earlier messages are put into it. */}
      {earlierNotice && (
        <div role="status" className="sr-only">
          {earlierNotice}
        </div>
      )}

      {showJump && (
        <button
          type="button"
          onClick={jumpToLatest}
          aria-label="Jump to latest"
          className="absolute bottom-3 left-1/2 flex -translate-x-1/2 cursor-pointer items-center gap-[6px] rounded-full bg-white px-3 py-[6px] text-xs font-bold"
          style={{
            color: KW.teal500,
            border: `1px solid ${KW.line2}`,
            boxShadow: '0 4px 14px rgba(20,40,70,.16)',
            fontFamily: 'inherit',
          }}
        >
          <ArrowDownOutlined />
          Jump to latest
        </button>
      )}
    </div>
  )
}

export default observer(MessageList)
